/**
 * Lossless WAV trim — sample-accurate, no decoding.
 *
 * WAV (RIFF/WAVE) is a header + raw PCM samples. To trim:
 *   1. Parse RIFF header + 'fmt ' chunk → get sample rate, channels, bytes/sample.
 *   2. Find 'data' chunk → byte offset of first sample.
 *   3. Convert (startSec, endSec) → byte offsets within data chunk.
 *   4. Write a new RIFF/WAVE header with corrected sizes + the trimmed data.
 *
 * Memory: O(file_size). For a 100 MB WAV that's 100 MB — same as the source.
 * No 4× PCM blow-up like the decode-render-encode path. Future phase: stream
 * via FileReader.readAsArrayBuffer chunks.
 *
 * Caveats:
 *   - PCM only (format code 1). FLOAT (format code 3) also supported.
 *   - Other RIFF subchunks (LIST/INFO, BEXT, cue points, etc.) are dropped.
 *     The output WAV has only fmt + data, which every player accepts.
 *   - Sample-accurate: the cut lands on a sample boundary (multiple of
 *     bytesPerSample × channels). Far more accurate than MP3 frame-level.
 *
 * Usage:
 *     const r = await window.WavFrameTrim.trim(blob, startSec, endSec);
 *     // r.blob — trimmed WAV
 *     // r.actualStart, r.actualEnd — sample-snapped boundaries
 *     // r.sampleRate, r.channels, r.bitsPerSample — informational
 */
(function () {
    'use strict';

    function readUint32LE(bytes, offset) {
        return (bytes[offset]) |
               (bytes[offset + 1] << 8) |
               (bytes[offset + 2] << 16) |
               (bytes[offset + 3] << 24);
    }
    function readUint16LE(bytes, offset) {
        return (bytes[offset]) | (bytes[offset + 1] << 8);
    }
    function writeUint32LE(bytes, offset, val) {
        bytes[offset]     = val & 0xFF;
        bytes[offset + 1] = (val >> 8) & 0xFF;
        bytes[offset + 2] = (val >> 16) & 0xFF;
        bytes[offset + 3] = (val >> 24) & 0xFF;
    }
    function writeUint16LE(bytes, offset, val) {
        bytes[offset]     = val & 0xFF;
        bytes[offset + 1] = (val >> 8) & 0xFF;
    }
    function writeAscii(bytes, offset, str) {
        for (var i = 0; i < str.length; i++) bytes[offset + i] = str.charCodeAt(i);
    }
    function isAscii(bytes, offset, str) {
        if (offset + str.length > bytes.length) return false;
        for (var i = 0; i < str.length; i++) {
            if (bytes[offset + i] !== str.charCodeAt(i)) return false;
        }
        return true;
    }

    /**
     * Parse RIFF/WAVE header, return {fmt, dataOffset, dataLength} or throw.
     */
    function parseWav(bytes) {
        if (bytes.length < 44) throw new Error('wav-trim: file too short');
        if (!isAscii(bytes, 0, 'RIFF')) throw new Error('wav-trim: not RIFF');
        if (!isAscii(bytes, 8, 'WAVE')) throw new Error('wav-trim: not WAVE');

        // Walk subchunks starting at byte 12.
        var pos = 12;
        var fmt = null;
        var dataOffset = -1;
        var dataLength = -1;

        while (pos + 8 <= bytes.length) {
            var chunkId = String.fromCharCode(bytes[pos], bytes[pos+1], bytes[pos+2], bytes[pos+3]);
            var chunkSize = readUint32LE(bytes, pos + 4);
            var chunkDataStart = pos + 8;

            if (chunkId === 'fmt ') {
                if (chunkSize < 16) throw new Error('wav-trim: fmt chunk too small');
                fmt = {
                    formatCode: readUint16LE(bytes, chunkDataStart),
                    channels: readUint16LE(bytes, chunkDataStart + 2),
                    sampleRate: readUint32LE(bytes, chunkDataStart + 4),
                    byteRate: readUint32LE(bytes, chunkDataStart + 8),
                    blockAlign: readUint16LE(bytes, chunkDataStart + 12),
                    bitsPerSample: readUint16LE(bytes, chunkDataStart + 14),
                };
                if (fmt.formatCode !== 1 && fmt.formatCode !== 3 && fmt.formatCode !== 0xFFFE) {
                    throw new Error('wav-trim: unsupported format code ' + fmt.formatCode);
                }
                if (fmt.formatCode === 0xFFFE && chunkSize >= 40) {
                    // EXTENSIBLE — read the SubFormat GUID's first 2 bytes (the actual format).
                    var sub = readUint16LE(bytes, chunkDataStart + 24);
                    if (sub !== 1 && sub !== 3) {
                        throw new Error('wav-trim: unsupported EXTENSIBLE subformat ' + sub);
                    }
                }
                // The whole chunk, id and size included, goes into the output as is
                // (2026-10-09): rebuilt as a plain 16-byte one it turned EXTENSIBLE into
                // tag 1 — a 5.1 file lost its speaker mask, a 32-bit FLOAT one came out
                // labelled as integers and read back as noise.
                fmt.chunk = bytes.slice(pos, chunkDataStart + chunkSize + (chunkSize & 1));
            } else if (chunkId === 'data') {
                dataOffset = chunkDataStart;
                dataLength = chunkSize;
                break; // We have everything we need.
            }
            // Chunks are padded to even length.
            pos = chunkDataStart + chunkSize + (chunkSize & 1);
        }

        if (!fmt) throw new Error('wav-trim: no fmt chunk');
        if (dataOffset < 0) throw new Error('wav-trim: no data chunk');
        // If 'data' chunkSize was 0xFFFFFFFF (streaming WAVs), recompute from EOF.
        if (dataLength <= 0 || dataOffset + dataLength > bytes.length) {
            dataLength = bytes.length - dataOffset;
        }
        return { fmt: fmt, dataOffset: dataOffset, dataLength: dataLength };
    }

    /**
     * Lossless WAV trim.
     * @param {Blob} blob - input WAV
     * @param {number} startSec - trim start in seconds
     * @param {number} endSec - trim end in seconds
     * @returns {Promise<{blob:Blob, actualStart:number, actualEnd:number, sampleRate:number, channels:number, bitsPerSample:number}>}
     */
    async function wavFrameTrim(blob, startSec, endSec) {
        if (!blob || typeof blob.arrayBuffer !== 'function') {
            throw new Error('wav-trim: blob required');
        }
        if (typeof startSec !== 'number' || typeof endSec !== 'number'
            || startSec < 0 || endSec <= startSec) {
            throw new Error('wav-trim: invalid time range');
        }

        var ab = await blob.arrayBuffer();
        var bytes = new Uint8Array(ab);

        var info = parseWav(bytes);
        var fmt = info.fmt;

        var totalSamples = Math.floor(info.dataLength / fmt.blockAlign);
        var totalDuration = totalSamples / fmt.sampleRate;

        // Snap to sample boundaries (blockAlign = bytesPerSample × channels).
        var startSample = Math.max(0, Math.floor(startSec * fmt.sampleRate));
        var endSample = Math.min(totalSamples, Math.ceil(endSec * fmt.sampleRate));
        if (endSample <= startSample) {
            throw new Error('wav-trim: empty selection');
        }

        var startByte = info.dataOffset + startSample * fmt.blockAlign;
        var endByte = info.dataOffset + endSample * fmt.blockAlign;
        var newDataLength = endByte - startByte;

        var actualStart = startSample / fmt.sampleRate;
        var actualEnd = endSample / fmt.sampleRate;

        // New RIFF/WAVE header: the source's own fmt chunk, then data (44 bytes for a
        // plain 16-byte fmt — byte-identical to the old minimal header).
        var fc = fmt.chunk, headerSize = 12 + fc.length + 8;
        var header = new Uint8Array(headerSize);
        writeAscii(header, 0, 'RIFF');
        writeUint32LE(header, 4, headerSize - 8 + newDataLength); // RIFF chunk size
        writeAscii(header, 8, 'WAVE');
        header.set(fc, 12);
        writeAscii(header, 12 + fc.length, 'data');
        writeUint32LE(header, 16 + fc.length, newDataLength);

        var outBlob = new Blob(
            [header, blob.slice(startByte, endByte)],
            { type: 'audio/wav' }
        );

        return {
            blob: outBlob,
            actualStart: actualStart,
            actualEnd: actualEnd,
            sampleRate: fmt.sampleRate,
            channels: fmt.channels,
            bitsPerSample: fmt.bitsPerSample,
            totalDuration: totalDuration,
        };
    }

    window.WavFrameTrim = {
        trim: wavFrameTrim,
        parseWav: parseWav,
    };
})();
